import { Suspense, lazy, useEffect } from 'react'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useUser, useOrganization } from '@clerk/clerk-react'
import * as Sentry from '@sentry/react'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { CookieConsent } from '@/components/CookieConsent'
import { usePageview } from '@/hooks/usePageview'
import { PendingOrganizationGate } from '@/components/auth/PendingOrganizationGate'
import { RequireEnablerSubmission } from '@/components/auth/RequireEnablerSubmission'
import { RequireSimpleAdminAuth } from '@/components/auth/RequireSimpleAdminAuth'
import { SupabaseEnvironmentProvider } from '@/contexts/SupabaseEnvironmentContext'
import { SimpleAdminAuthProvider } from '@/contexts/SimpleAdminAuthContext'
import { setSentryUser, clearSentryUser, setSentryOrganization } from '@/lib/sentry'
import GCCPlatformPage from './pages/GCCPlatformPage'

const Index = lazy(() => import('./pages/Index'))
const MarketplacePreview = lazy(() => import('./pages/MarketplacePreview'))
const AgentDetails = lazy(() => import('./pages/AgentDetails'))
const ListYourAgent = lazy(() => import('./pages/ListYourAgent'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const GetStartedPage = lazy(() => import('./pages/GetStartedPage'))
const BenchmarksPage = lazy(() => import('./pages/BenchmarksPage'))
const ToolsPage = lazy(() => import('./pages/ToolsPage'))
const ProvidersPage = lazy(() => import('./pages/ProvidersPage'))
const ProviderDirectoryListingsPage = lazy(() => import('./pages/ProviderDirectoryListingsPage'))
const ExplorePlatformPage = lazy(() => import('./pages/ExplorePlatformPage'))
const CommunityPage = lazy(() => import('./pages/CommunityPage'))
const ThoughtLeadershipPage = lazy(() => import('./pages/ThoughtLeadershipPage'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'))
const UseCasesPage = lazy(() => import('./pages/UseCasesPage'))
const UseCaseAgentDetails = lazy(() => import('./pages/UseCaseAgentDetails'))
const AgenticAIThoughtbookPage = lazy(() => import('./pages/AgenticAIThoughtbookPage'))
const ProviderDashboard = lazy(() => import('./pages/ProviderDashboard'))
const EditAgent = lazy(() => import('./pages/EditAgent'))
const AuthPage = lazy(() => import('./pages/AuthPage'))
const SelfAssessmentPage = lazy(() => import('./pages/SelfAssessment'))
const SelfAssessmentResultPage = lazy(() => import('./pages/SelfAssessmentResult'))
const SelfAssessmentLanding = lazy(() => import('./pages/tools/SelfAssessmentLanding'))
const CharterLanding = lazy(() => import('./pages/tools/CharterLanding'))
const SkillsTaxonomyLanding = lazy(() => import('./pages/tools/SkillsTaxonomyLanding'))
const WorkflowKitLanding = lazy(() => import('./pages/tools/WorkflowKitLanding'))
const BusinessCaseLanding = lazy(() => import('./pages/tools/BusinessCaseLanding'))
const AIReadinessAssessmentLanding = lazy(() => import('./pages/tools/AIReadinessAssessmentLanding'))
const AIUseCaseCaptureLanding = lazy(() => import('./pages/tools/AIUseCaseCaptureLanding'))
const AIUseCasePrioritizationLanding = lazy(() => import('./pages/tools/AIUseCasePrioritizationLanding'))
const GCCDashboard = lazy(() => import('./pages/GCCDashboard'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const ProblemStatementsPage = lazy(() => import('./pages/ProblemStatementsPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'))
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'))
const DataDeletionPage = lazy(() => import('./pages/DataDeletionPage'))
const NotFound = lazy(() => import('./pages/NotFound'))

// QueryClient with Sentry error capture for queries and mutations
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.captureException(error, {
        tags: { type: 'query' },
        extra: { queryKey: JSON.stringify(query.queryKey) },
      })
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      Sentry.captureException(error, {
        tags: { type: 'mutation' },
        extra: { mutationKey: JSON.stringify(mutation.options.mutationKey) },
      })
    },
  }),
})

const PageFallback = () => (
  <div className="flex min-h-screen items-center justify-center bg-background text-slate-500">
    <span className="animate-pulse text-sm tracking-wide">Loading experience…</span>
  </div>
)

const routes = [
  { path: '/', element: <GCCPlatformPage /> },
  { path: '/orbyt', element: <Index /> },
  { path: '/agentsphere', element: <Navigate to="/orbyt" replace /> },
  { path: '/marketplace', element: <MarketplacePreview /> },
  { path: '/marketplace/agent/:agentId', element: <AgentDetails /> },
  { path: '/list-your-agent', element: <ListYourAgent /> },
  {
    path: '/admin/:secretToken',
    element: (
      <SimpleAdminAuthProvider>
        <RequireSimpleAdminAuth>
          <SupabaseEnvironmentProvider>
            <AdminDashboard />
          </SupabaseEnvironmentProvider>
        </RequireSimpleAdminAuth>
      </SimpleAdminAuthProvider>
    ),
  },
  { path: '/problems', element: <ProblemStatementsPage /> },
  { path: '/get-started', element: <GetStartedPage /> },
  { path: '/benchmarks', element: <BenchmarksPage /> },
  { path: '/tools', element: <ToolsPage /> },
  { path: '/providers', element: <ProvidersPage /> },
  { path: '/providers/directory', element: <ProviderDirectoryListingsPage /> },
  { path: '/pricing', element: <PricingPage /> },
  { path: '/explore-platform', element: <ExplorePlatformPage /> },
  { path: '/community', element: <CommunityPage /> },
  { path: '/thought-leadership', element: <ThoughtLeadershipPage /> },
  { path: '/thought-leadership/:slug', element: <BlogPostPage /> },
  { path: '/use-cases', element: <UseCasesPage /> },
  { path: '/use-cases/agent/:id', element: <UseCaseAgentDetails /> },
  { path: '/agentic-ai-thoughtbook', element: <AgenticAIThoughtbookPage /> },
  { path: '/agentic-ai-thoughtbook/:topicId', element: <AgenticAIThoughtbookPage /> },
  { path: '/provider', element: <RequireEnablerSubmission><ProviderDashboard /></RequireEnablerSubmission> },
  { path: '/provider/agents/:agentId/edit', element: <RequireEnablerSubmission><EditAgent /></RequireEnablerSubmission> },
  { path: '/gcc-dashboard', element: <GCCDashboard /> },
  { path: '/auth', element: <AuthPage /> },
  { path: '/tools/self-assessment', element: <SelfAssessmentLanding /> },
  { path: '/tools/charter', element: <CharterLanding /> },
  { path: '/tools/skills-taxonomy', element: <SkillsTaxonomyLanding /> },
  { path: '/tools/workflow-kit', element: <WorkflowKitLanding /> },
  { path: '/tools/business-case', element: <BusinessCaseLanding /> },
  { path: '/tools/ai-readiness-assessment', element: <AIReadinessAssessmentLanding /> },
  { path: '/tools/ai-use-case-capture', element: <AIUseCaseCaptureLanding /> },
  { path: '/tools/ai-use-case-prioritization', element: <AIUseCasePrioritizationLanding /> },
  { path: '/self-assessment', element: <SelfAssessmentPage /> },
  { path: '/self-assessment/result/:id', element: <SelfAssessmentResultPage /> },
  { path: '/privacy', element: <PrivacyPolicyPage /> },
  { path: '/terms', element: <TermsOfServicePage /> },
  { path: '/cookies', element: <CookiePolicyPage /> },
  { path: '/data-deletion', element: <DataDeletionPage /> },
  { path: '*', element: <NotFound /> },
]

const App = () => {
  usePageview()

  // Set Sentry user context when Clerk auth loads
  const { user, isLoaded: userLoaded } = useUser()
  const { organization, isLoaded: orgLoaded } = useOrganization()

  useEffect(() => {
    if (!userLoaded || !orgLoaded) return

    if (user) {
      setSentryUser({
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        username: user.username,
      })

      if (organization) {
        setSentryOrganization({
          id: organization.id,
          name: organization.name,
        })
      }
    } else {
      clearSentryUser()
    }
  }, [userLoaded, orgLoaded, user, organization])

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ErrorBoundary>
          <Suspense fallback={<PageFallback />}>
            <PendingOrganizationGate>
              <Routes>
                {routes.map(route => (
                  <Route key={route.path} path={route.path} element={route.element} />
                ))}
              </Routes>
            </PendingOrganizationGate>
          </Suspense>
          <CookieConsent />
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

export default App
